/**
 * Evaluación del recordatorio de respaldo (Req. 7).
 *
 * En un iPhone los datos locales pueden desaparecer sin aviso, y no hay copia en
 * la nube. Safari tampoco permite respaldar de forma automática, así que este
 * aviso es la última línea de defensa: su lógica vive en el dominio y tiene
 * pruebas, en lugar de quedar escondida en un componente.
 *
 * Sin notificaciones del sistema operativo: el aviso se muestra dentro de la app.
 *
 * Origen: Control de Negocio de Comida; los textos ahora llevan tildes.
 */

export type NivelRespaldo = 'ok' | 'aviso' | 'alerta';

export interface EstadoRespaldo {
  /** Días completos desde el último respaldo. null si nunca se ha hecho. */
  diasDesdeRespaldo: number | null;
  nivel: NivelRespaldo;
  /** true = mostrar el aviso en todas las pantallas (salvo Respaldos). */
  debeAvisar: boolean;
  /** Texto listo para mostrar. */
  mensaje: string;
  /** Texto secundario que explica el riesgo. */
  detalle: string | null;
}

export interface ParametrosRespaldo {
  /** Marca ISO del último respaldo exportado con éxito. */
  ultimoRespaldoEn: string | null;
  /** Si no hay datos capturados, no hay nada que perder ni que avisar (Req. 7.5). */
  hayDatos: boolean;
  /** Días tras los cuales avisar. Predeterminado 7 y ajustable (Req. 7.1, 7.4). */
  diasAviso?: number;
  /** Inyectable para poder probar sin depender del reloj. */
  ahora?: Date;
}

/** A partir de este umbral el aviso sube a jerarquía de alerta (Req. 7.1). */
export const DIAS_ALERTA = 14;

const MILISEGUNDOS_POR_DIA = 86_400_000;

export function diasDesde(iso: string, ahora: Date): number | null {
  const marca = Date.parse(iso);
  if (Number.isNaN(marca)) return null;
  const transcurridos = Math.floor((ahora.getTime() - marca) / MILISEGUNDOS_POR_DIA);
  return transcurridos < 0 ? 0 : transcurridos;
}

function plural(dias: number): string {
  return dias === 1 ? '1 día' : `${dias} días`;
}

export function evaluarRespaldo(parametros: ParametrosRespaldo): EstadoRespaldo {
  const { ultimoRespaldoEn, hayDatos } = parametros;
  const diasAviso = parametros.diasAviso ?? 7;
  const ahora = parametros.ahora ?? new Date();

  // Sin datos capturados no hay nada que respaldar todavía (Req. 7.5).
  if (!hayDatos) {
    return {
      diasDesdeRespaldo: ultimoRespaldoEn === null ? null : diasDesde(ultimoRespaldoEn, ahora),
      nivel: 'ok',
      debeAvisar: false,
      mensaje: 'Aún no hay datos que respaldar.',
      detalle: null,
    };
  }

  // Nunca se ha respaldado y ya hay información capturada (Req. 7.1).
  if (ultimoRespaldoEn === null) {
    return {
      diasDesdeRespaldo: null,
      nivel: 'alerta',
      debeAvisar: true,
      mensaje: 'Nunca has hecho un respaldo',
      detalle:
        'Tus datos solo existen en este teléfono. Si lo pierdes, borras el ícono o borras ' +
        'los datos del navegador, no habrá forma de recuperarlos.',
    };
  }

  const dias = diasDesde(ultimoRespaldoEn, ahora);

  // Fecha ilegible: se trata como si no hubiera respaldo, que es el lado seguro.
  if (dias === null) {
    return {
      diasDesdeRespaldo: null,
      nivel: 'alerta',
      debeAvisar: true,
      mensaje: 'No se pudo leer la fecha del último respaldo',
      detalle: 'Haz un respaldo nuevo para no arriesgar tus datos.',
    };
  }

  if (dias >= DIAS_ALERTA) {
    return {
      diasDesdeRespaldo: dias,
      nivel: 'alerta',
      debeAvisar: true,
      mensaje: `Último respaldo: hace ${plural(dias)}`,
      detalle: 'Ya pasó mucho tiempo. Haz un respaldo ahora para no arriesgar tus datos.',
    };
  }

  if (dias >= diasAviso) {
    return {
      diasDesdeRespaldo: dias,
      nivel: 'aviso',
      debeAvisar: true,
      mensaje: `Último respaldo: hace ${plural(dias)}`,
      detalle: 'Conviene hacer un respaldo.',
    };
  }

  return {
    diasDesdeRespaldo: dias,
    nivel: 'ok',
    debeAvisar: false,
    mensaje: dias === 0 ? 'Respaldo hecho hoy' : `Último respaldo: hace ${plural(dias)}`,
    detalle: null,
  };
}

/**
 * Antigüedad del último respaldo para Hoy (Req. 7.3): "Nunca", "hoy",
 * "hace 1 día" o "hace N días".
 */
export function textoUltimoRespaldo(ultimoRespaldoEn: string | null, ahora = new Date()): string {
  if (ultimoRespaldoEn === null) return 'Nunca';
  const dias = diasDesde(ultimoRespaldoEn, ahora);
  if (dias === null) return 'Fecha desconocida';
  if (dias === 0) return 'hoy';
  return `hace ${plural(dias)}`;
}

export interface SugerenciaActualizacion {
  sugerir: boolean;
  mensaje: string | null;
}

const MILISEGUNDOS_POR_HORA = 3_600_000;

/**
 * ¿Conviene respaldar antes de instalar una versión nueva? (Req. 2.4): sí, si
 * hay datos y el último respaldo tiene más de un día o nunca se ha hecho.
 * Es una sugerencia: actualizar nunca se bloquea por esto.
 */
export function sugerirRespaldoAntesDeActualizar(parametros: {
  ultimoRespaldoEn: string | null;
  hayDatos: boolean;
  ahora?: Date;
}): SugerenciaActualizacion {
  const { ultimoRespaldoEn, hayDatos } = parametros;
  const ahora = parametros.ahora ?? new Date();
  if (!hayDatos) return { sugerir: false, mensaje: null };

  if (ultimoRespaldoEn === null) {
    return {
      sugerir: true,
      mensaje: 'Todavía no tienes un respaldo. Conviene hacer uno antes de actualizar.',
    };
  }
  const marca = Date.parse(ultimoRespaldoEn);
  if (Number.isNaN(marca)) {
    return { sugerir: true, mensaje: 'Conviene hacer un respaldo antes de actualizar.' };
  }
  if (ahora.getTime() - marca <= 24 * MILISEGUNDOS_POR_HORA) return { sugerir: false, mensaje: null };

  const dias = Math.max(1, diasDesde(ultimoRespaldoEn, ahora) ?? 1);
  return {
    sugerir: true,
    mensaje: `Tu último respaldo es de hace ${plural(dias)}. Conviene hacer uno antes de actualizar.`,
  };
}

/** Texto para la pantalla de respaldos y para Hoy, que muestran la fecha (Req. 7.3). */
export function describirUltimoRespaldo(ultimoRespaldoEn: string | null): string {
  if (ultimoRespaldoEn === null) return 'Nunca';

  const marca = new Date(ultimoRespaldoEn);
  if (Number.isNaN(marca.getTime())) return 'Fecha desconocida';

  const p = (n: number) => String(n).padStart(2, '0');
  return (
    `${p(marca.getDate())}/${p(marca.getMonth() + 1)}/${marca.getFullYear()} ` +
    `a las ${p(marca.getHours())}:${p(marca.getMinutes())}`
  );
}
